import React, { useState, useEffect } from "react";
import { Query, Column, MTableBody } from "material-table";
import { Button, Paper } from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import { DefaultTable, ClubSelect, HelpscoutFab } from "components";
import { Court } from "api/courts";
import { getCourts, deleteCourts, postCourts } from "api/admin/courts";
import { Club } from "api/clubs";
import { useMounted } from "utils/hooks";
import uI18n from "utils/i18n";
import i18n from "./i18n";
import EditCourtDialog from "./EditCourtDialog";

// prop types
interface CourtsViewProps {
    club: Club | null,
}

// currency formatter
const formatter = Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// admin courts table
const CourtsView = ({ club }: CourtsViewProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [clubId, setClubId] = useState<string | undefined>();
    const [showEditCourtDialog, setShowEditCourtDialog] = useState<string | null>(null);

    // reset ui
    useEffect(() => setClubId(club?.id), [club]);

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<Court>) => {
        // paging
        const params: Parameters<typeof getCourts>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // sorting
        if (query.orderBy) {
            params.sortOrder = query.orderDirection.toUpperCase() as typeof params["sortOrder"];
            params.sortKey = query.orderBy.field as typeof params["sortKey"];
        }

        // filter
        params.query = query.search;
        params.clubId = clubId;

        return params;
    };

    // delete row handler (delete court)
    const onRowDelete = (oldData: Court) => new Promise<void>((resolve) => {
        deleteCourts({
            courtIds: [oldData.id],
        }).then(() => resolve()).catch((err) => { displayError(err); resolve(); });
    });

    // create court
    const onCreateCourt = async () => {
        await postCourts({ clubId: clubId! });
        refreshTable();
    };

    // table actions
    const tableActions = [
        ({ id }: Court) => ({
            icon: "edit",
            tooltip: i18n.edit,
            onClick: () => setShowEditCourtDialog(id),
        }),
        {
            disabled: !clubId,
            icon: "add_box",
            isFreeAction: true,
            tooltip: i18n.createCourt,
            onClick: onCreateCourt,
        },
    ];

    // table columns
    const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
    const tableColumns: Column<Court>[] = [
        {
            field: "courtName",
            title: i18n.name,
        },
        {
            field: "courtNumber",
            sorting: false,
            title: i18n.number,
        },
        {
            field: "clubName",
            title: i18n.club,
        },
        {
            field: "sportForms",
            render: ({ sportForms }) => sportForms.map((s) => uI18n.sportForms[s] || capitalize(s)).join(", "),
            sorting: false,
            title: i18n.sportForm,
        },
        {
            field: "bookingPriceCents",
            render: ({ bookingPriceCents }) => formatter.format(bookingPriceCents / 100),
            sorting: false,
            title: i18n.tier,
        },
    ];

    // add court button (if table empty)
    const AddCourtButton = () => (
        <tbody>
            <tr>
                <th colSpan={tableColumns.length + 1}>
                    <Button
                        color="primary"
                        startIcon={<AddIcon />}
                        style={{ margin: "64px 0" }}
                        variant="contained"
                        onClick={onCreateCourt}
                    >
                        {i18n.createCourt}
                    </Button>
                </th>
            </tr>
        </tbody>
    );

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [clubId]);

    // render
    return (
        <>
            <HelpscoutFab club={club} />

            {/* club select */}
            <Paper style={{ display: "flex", justifyContent: "flex-end" }}>
                <ClubSelect
                    clubId={clubId}
                    controlProps={{ style: { minWidth: "96px", marginRight: "8px" } }}
                    userRoles={["owner", "admin"]}
                    onChange={setClubId}
                />
            </Paper>

            {/* courts table */}
            <DefaultTable
                actions={tableActions}
                columns={tableColumns}
                components={{
                    Body: (props: any) => (props.renderData.length === 0 ? <AddCourtButton /> : <MTableBody {...props} />), // eslint-disable-line
                }}
                data={(query) => new Promise((resolve) => {
                    const params = convertQueryToParams(query);
                    getCourts(params)
                        .then((response) => resolve({
                            ...response,
                            page: query.page,
                        }))
                        .catch((err) => {
                            displayError(err);
                            resolve({ data: [], page: 0, totalCount: 0 });
                        });
                })}
                editable={{ onRowDelete }}
                tableRef={tableRef}
                title={i18n.courts}
            />

            {/* edit court dialog */}
            <EditCourtDialog
                club={club}
                court={tableRef.current?.dataManager.data.find((c: Court) => c.id === showEditCourtDialog)}
                show={!!showEditCourtDialog}
                onClose={() => setShowEditCourtDialog(null)}
                onRefreshCourtsTable={refreshTable}
            />
        </>
    );
};

export default CourtsView;
